import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../../form/Form.css';
import './ParticipantGroupEditor.css';
import './RoleSettingEditor.css';
import api from '../../wrapper/API';
import ActionButton from '../../input/ActionButton';
import CustomTogglebox from '../../input/CustomTogglebox';
import ParticipantGroupDot from '../ParticipantGroupDot';
import ParticipantGroupColorPicker from './ParticipantGroupColorPicker';
import GroupStatusBadge from './GroupStatusBadge';
import { useSuccessBar } from '../../context/SuccessBarContext';
import { COLOR_KEY_LABELS, ROLE, ROLE_COLOR_CHOICES, ROLE_PERMISSIONS_TEXT } from '../../../util/participantGroups';
import {
  apiErrorMessage, PRICE_MAX, ROLE_NAME_MAX, roleFormFromSetting, sameRoleForm, toRoleSaveRequest,
  validateRoleForm,
} from './groupAdminUtils';

// "„A“", "„A“ und „B“", "„A“, „B“ und „C“"
const quotedList = (names) => {
  const quoted = names.map(n => `„${n}“`);
  return quoted.length > 1 ? `${quoted.slice(0, -1).join(', ')} und ${quoted[quoted.length - 1]}` : quoted[0] ?? '';
};

/**
 * Editor of one role (section "Rollen" on /groups): display name, dot color, "Kann Spieltage leiten" and
 * the salary per led event — one "Speichern" (PUT /Role/UpdateSettings/{role}). No tabs: a role has only
 * these settings for now. The page keys this component by role, so switching always starts fresh.
 * onSaved(rawSetting) publishes the response app-wide and returns the normalized setting (null = none).
 */
function RoleSettingEditor({ setting, roles, groups, isOffline, onBack, onDirtyChange, onSaved }) {
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();

  // Set in the effect body as well: StrictMode runs mount → cleanup → mount in development.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const baseline = useMemo(() => roleFormFromSetting(setting), [setting]);
  const [form, setForm] = useState(baseline);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const dirty = !sameRoleForm(form, baseline);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const otherRoles = useMemo(() => roles.filter(r => r.role !== setting.role), [roles, setting.role]);
  const errors = validateRoleForm(form, otherRoles);
  const shownErrors = submitted ? errors : {};

  // Same color as another role or an active group: allowed, but the dots look alike → warn.
  const clashRoles = otherRoles.filter(r => r.colorKey === form.colorKey);
  const clashGroups = groups.filter(g => !g.isArchived && g.colorKey === form.colorKey);
  // Nobody could lead events anymore → events that require a leader stay drafts.
  const noLeaderLeft = !form.canLeadEvents && otherRoles.every(r => !r.canLeadEvents);

  const setField = useCallback((key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setSaveError('');
  }, []);

  // Switching leadership off hides the salary → drop unsaved salary edits (the saved value is kept).
  const setCanLead = (checked) => {
    setForm(prev => ({ ...prev, canLeadEvents: checked, leaderSalary: checked ? prev.leaderSalary : baseline.leaderSalary }));
    setSaveError('');
  };

  const handleSave = async () => {
    if (saving || isOffline) return;
    setSubmitted(true);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    setSaveError('');
    startSuccessBarLoading('Rolle wird gespeichert');
    try {
      const res = await api.put(`/Role/UpdateSettings/${setting.role}`, toRoleSaveRequest(form, setting));
      // The response is the saved setting: server-normalized values become the new form state.
      const saved = onSaved(res.data);
      startSuccessBarSuccess('Rolle gespeichert');
      if (alive.current) {
        if (saved) setForm(roleFormFromSetting(saved));
        setSaving(false);
        setSubmitted(false);
      }
    } catch (err) {
      closeSuccessBar();
      if (alive.current) {
        setSaveError(apiErrorMessage(err, 'Die Rolle konnte nicht gespeichert werden.'));
        setSaving(false);
      }
    }
  };

  const handleDiscard = () => {
    setForm(baseline);
    setSubmitted(false);
    setSaveError('');
  };

  // Header dot in the (unsaved) form color, like the group editor.
  const headerDot = { id: 0, name: form.name || setting.name, colorKey: form.colorKey };

  const clashLines = [
    clashRoles.length > 0 && `Die Rolle ${quotedList(clashRoles.map(r => r.name))} hat dieselbe Farbe – beide Rollen sehen am Punkt gleich aus.`,
    clashGroups.length > 0 && (clashGroups.length === 1
      ? `Die Gruppe „${clashGroups[0].name}“ hat dieselbe Farbe – Personen mit dieser Rolle sehen dann aus wie Personen dieser Gruppe ohne Rolle.`
      : `Die Gruppen ${quotedList(clashGroups.map(g => g.name))} haben dieselbe Farbe – Personen mit dieser Rolle sehen dann aus wie Personen dieser Gruppen ohne Rolle.`),
  ].filter(Boolean);

  return (
    <section className="groupEditor roleEditor" aria-label={`Rolle ${setting.name}`}>
      <button type="button" className="groupEditorBack" onClick={onBack}>
        <span className="material-symbols-rounded" aria-hidden="true">arrow_back</span>
        Gruppen &amp; Rollen
      </button>

      <div className="groupEditorHeader">
        <div className="groupEditorTitleRow">
          <span className="groupEditorTitleDot">
            <ParticipantGroupDot participantGroup={headerDot} size="md" withTooltip={false} />
          </span>
          <h3 className="groupEditorTitle">{setting.name}</h3>
          <GroupStatusBadge kind="role" />
        </div>
      </div>

      <div className="groupEditorPanel roleEditorBody">
        <div className="groupTabBody">
          <section className="groupEditorSection">
            <h4 className="groupEditorSectionTitle">Bezeichnung</h4>
            <div className="formFieldWrapper">
              <label className="formFieldLabel" htmlFor="roleName">Anzeigename</label>
              <input
                id="roleName"
                type="text"
                maxLength={ROLE_NAME_MAX}
                value={form.name}
                onChange={e => setField('name', e.target.value)}
                placeholder={setting.role === ROLE.Admin ? 'z. B. Admin' : 'z. B. Orga'}
                aria-invalid={shownErrors.name ? 'true' : undefined}
                className={shownErrors.name ? 'isInvalid' : ''}
              />
            </div>
            {shownErrors.name && <p className="groupEditorFieldError">{shownErrors.name}</p>}
            <p className="formFieldHint">
              Erscheint überall im Tool neben dem Punkt, z. B. „{form.name.trim() || setting.name} · Mitglied“.
            </p>
          </section>

          <section className="groupEditorSection">
            <h4 className="groupEditorSectionTitle">Farbe</h4>
            <ParticipantGroupColorPicker
              value={form.colorKey}
              onChange={key => setField('colorKey', key)}
              keys={ROLE_COLOR_CHOICES}
              ariaLabel="Farbe der Rolle"
            />
            {shownErrors.colorKey && <p className="groupEditorFieldError">{shownErrors.colorKey}</p>}
            {clashLines.length > 0 && (
              <div className="groupEditorWarning roleEditorWarning">
                {clashLines.map(line => <p key={line}>{line}</p>)}
              </div>
            )}
            <p className="formFieldHint">
              Der Punkt von Personen mit dieser Rolle zeigt {COLOR_KEY_LABELS[form.colorKey] ?? 'diese Farbe'} statt
              der Gruppenfarbe. Gruppen können diese Farbe dann nicht mehr wählen.
            </p>
          </section>

          <section className="groupEditorSection">
            <h4 className="groupEditorSectionTitle">Spieltagleitung</h4>
            <CustomTogglebox
              placeholder="Kann Spieltage leiten"
              checked={form.canLeadEvents}
              onChange={e => setCanLead(e.target.checked)}
            />
            {form.canLeadEvents ? (
              <React.Fragment>
                <div className="formFieldWrapper roleEditorSalary">
                  <label className="formFieldLabel" htmlFor="roleLeaderSalary">Lohn pro geleitetem Spieltag (€)</label>
                  <input
                    id="roleLeaderSalary"
                    type="number" min={0} max={PRICE_MAX} step={0.01} inputMode="decimal"
                    value={form.leaderSalary}
                    onChange={e => setField('leaderSalary', e.target.value)}
                    aria-invalid={shownErrors.leaderSalary ? 'true' : undefined}
                    className={shownErrors.leaderSalary ? 'isInvalid' : ''}
                  />
                </div>
                {shownErrors.leaderSalary && <p className="groupEditorFieldError">{shownErrors.leaderSalary}</p>}
                <p className="formFieldHint">
                  Spieltagleiter zahlen nie für ihre Teilnahme – der Lohn fließt in die Statistik ein („Lohn
                  Spieltagleiter“). 0 = kein Lohn.
                </p>
              </React.Fragment>
            ) : (
              <p className="formFieldHint">
                Personen mit dieser Rolle können nicht als Spieltagleiter eingetragen werden. Bereits eingetragene
                Leitungen lassen sich weiterhin austragen.
              </p>
            )}
            {noLeaderLeft && (
              <p className="groupEditorWarning">
                Dann kann keine Rolle mehr Spieltage leiten – Spieltage, die eine Leitung brauchen, bleiben im Entwurf.
              </p>
            )}
          </section>

          <section className="groupEditorSection">
            <h4 className="groupEditorSectionTitle">Rechte</h4>
            <p className="groupEditorExplanation">{ROLE_PERMISSIONS_TEXT[setting.role]}</p>
            <p className="formFieldHint">Die Rechte der Rollen sind fest vorgegeben.</p>
          </section>
        </div>
      </div>

      <div className="groupEditorFooter">
        {saveError && <p className="newError groupEditorSaveError" role="alert">{saveError}</p>}
        {submitted && Object.keys(errors).length > 0 && (
          <p className="groupEditorFieldError">Bitte die markierten Angaben prüfen.</p>
        )}
        <div className="groupEditorFooterBar">
          <span className="groupEditorFooterStatus">
            {isOffline ? (
              <span className="formOfflineHint">
                <span className="material-symbols-rounded formOfflineHintIcon" aria-hidden="true">cloud_off</span>
                Offline nicht verfügbar
              </span>
            ) : dirty ? (
              <span className="groupEditorDirty">Ungespeicherte Änderungen</span>
            ) : null}
          </span>
          <div className="groupEditorFooterActions">
            {dirty && <ActionButton label="Verwerfen" onClick={handleDiscard} disabled={saving} />}
            <ActionButton
              icon="check"
              label="Speichern"
              type={3}
              onClick={handleSave}
              disabled={isOffline || saving || !dirty}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export default RoleSettingEditor;
